/**
 * consoleCapture.js
 * Author: Markus.van.Kempen@gmail.com
 * Date: 2nd Nov 2025
 */

// Global console log capture utility
// This captures console logs from the moment the page loads

class ConsoleCapture {
  constructor(maxLogs = 100) {
    this.logs = [];
    this.maxLogs = maxLogs;
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info
    };
    this.isCapturing = false;
  }

  // Start capturing console logs
  start() {
    if (this.isCapturing) {
      console.log('[ConsoleCapture] Already capturing, skipping duplicate initialization');
      return;
    }
    this.isCapturing = true;

    const self = this;

    // Override console.log
    console.log = function(...args) {
      self.captureLog('log', args);
      self.originalConsole.log.apply(console, args);
    };

    // Override console.warn
    console.warn = function(...args) {
      self.captureLog('warn', args);
      self.originalConsole.warn.apply(console, args);
    };

    // Override console.error
    console.error = function(...args) {
      self.captureLog('error', args);
      self.originalConsole.error.apply(console, args);
    };

    // Override console.info
    console.info = function(...args) {
      self.captureLog('info', args);
      self.originalConsole.info.apply(console, args);
    };

    // Use original console to avoid capturing this message
    this.originalConsole.log('[ConsoleCapture] Started capturing console logs');
  }

  // Capture a log entry
  captureLog(type, args) {
    try {
      const message = Array.from(args).map(arg => {
        if (arg === null) return 'null';
        if (arg === undefined) return 'undefined';
        if (typeof arg === 'object') {
          try {
            // Handle circular references and complex objects
            return JSON.stringify(arg, this.getCircularReplacer(), 2);
          } catch (e) {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');

      // Don't capture ConsoleCapture's own messages to avoid meta-logging
      if (message.includes('[ConsoleCapture]')) {
        return;
      }

      this.logs.push({
        type,
        message,
        timestamp: new Date().toISOString(),
        timeFormatted: new Date().toLocaleTimeString()
      });

      // Keep only last N logs to prevent memory issues
      if (this.logs.length > this.maxLogs) {
        this.logs.shift();
      }
    } catch (error) {
      // Silently fail to avoid recursion
    }
  }

  // Handle circular references in objects
  getCircularReplacer() {
    const seen = new WeakSet();
    return (key, value) => {
      if (typeof value === "object" && value !== null) {
        if (seen.has(value)) {
          return "[Circular Reference]";
        }
        seen.add(value);
      }
      return value;
    };
  }

  // Get all captured logs
  getLogs() {
    return [...this.logs];
  }

  // Get last N logs
  getRecentLogs(count = 20) {
    return this.logs.slice(-count);
  }

  // Clear all logs
  clear() {
    this.logs = [];
  }

  // Stop capturing and restore original console
  stop() {
    if (!this.isCapturing) return;

    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;

    this.isCapturing = false;
    this.originalConsole.log('[ConsoleCapture] Stopped capturing console logs');
  }

  // Get count of logs by type
  getLogStats() {
    const stats = {
      log: 0,
      warn: 0,
      error: 0,
      info: 0,
      total: this.logs.length
    };

    this.logs.forEach(log => {
      if (stats[log.type] !== undefined) {
        stats[log.type]++;
      }
    });

    return stats;
  }
}

// Create and export a singleton instance
const consoleCapture = new ConsoleCapture(100);

// Auto-start capturing when this module loads
if (typeof window !== 'undefined') {
  consoleCapture.start();
}

export default consoleCapture;

